import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { WellCatImportResult } from '../../models/well-cat.models';
import { WellCatService } from '../../services/well-cat.service';

@Component({
  selector: 'app-well-cat-import',
  templateUrl: './well-cat-import.component.html',
  standalone: false
})
export class WellCatImportComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() importComplete = new EventEmitter<WellCatImportResult>();

  public readonly selectedFile = signal<File | null>(null);
  public readonly importResult = signal<WellCatImportResult | null>(null);
  public readonly isImporting = signal(false);

  public readonly importTrajectory = signal(true);
  public readonly importWellboreGeometry = signal(true);
  public readonly importFormationMarkers = signal(true);
  public readonly importPressureWindow = signal(true);
  public readonly importTemperature = signal(true);

  public readonly canImport = computed(() =>
    !!this.selectedFile() &&
    (this.importTrajectory() ||
      this.importWellboreGeometry() ||
      this.importFormationMarkers() ||
      this.importPressureWindow() ||
      this.importTemperature())
  );

  constructor(
    private readonly wellCatService: WellCatService,
    private readonly notifications: AppNotificationService
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile.set(file);
  }

  import(): void {
    const file = this.selectedFile();
    if (!file) {
      return;
    }

    this.isImporting.set(true);

    this.wellCatService.importFromFile(file, {
      importTrajectory: this.importTrajectory(),
      importWellboreGeometry: this.importWellboreGeometry(),
      importFormationMarkers: this.importFormationMarkers(),
      importPressureWindow: this.importPressureWindow(),
      importTemperature: this.importTemperature()
    }).subscribe({
      next: result => {
        this.isImporting.set(false);
        this.importResult.set(result);
        this.importComplete.emit(result);
        this.notifications.showSuccess(result.summary || 'WellCat import completed.');
      },
      error: () => {
        this.isImporting.set(false);
        this.notifications.showError('WellCat import failed. Please review file and try again.');
      }
    });
  }

  copyReport(): void {
    const result = this.importResult();
    if (!result?.report) {
      return;
    }

    const lines: string[] = ['WellCat Import Report', ''];
    const report = result.report;

    if (report.applied.length > 0) {
      lines.push('✅ Applied');
      report.applied.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.reviewRequired.length > 0) {
      lines.push('⚠️ Review recommended');
      report.reviewRequired.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.actionRequired.length > 0) {
      lines.push('✏️ Action required');
      report.actionRequired.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.notImported.length > 0) {
      lines.push('ℹ️ Not imported');
      report.notImported.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
    }

    navigator.clipboard.writeText(lines.join('\n')).then(() =>
      this.notifications.showSuccess('Import report copied to clipboard.'));
  }

  close(): void {
    this.importResult.set(null);
    this.selectedFile.set(null);
    this.visibleChange.emit(false);
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
  }
}
